import { useMemo } from "react";
import { StyleSheet, Text, View } from "react-native";

import { fontFamily, radius } from "@passfirst/ui";

import { type Colors, useColors } from "../theme";

interface WeakTopic {
  topicSlug: string;
  label: string;
  recentAccuracy: number | null;
}

interface Props {
  topics: WeakTopic[];
}

// "Where to focus next" — top 3 weakest topics with severity-colored
// mini-bars. Brick for very weak, amber for middling, green for ≥80%.
// Mirrors the home dashboard's mastery list visually.
export function WeakTopicsList({ topics }: Props): JSX.Element | null {
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  if (topics.length === 0) return null;

  const colorForAccuracy = (pct: number): string => {
    if (pct < 50) return colors.error;
    if (pct < 80) return colors.caution;
    return colors.success;
  };

  return (
    <View style={styles.section}>
      <Text style={styles.eyebrow}>WHERE TO FOCUS NEXT</Text>
      {topics.map((t) => {
        const pct = Math.max(
          0,
          Math.min(100, Math.round((t.recentAccuracy ?? 0) * 100)),
        );
        return (
          <View key={t.topicSlug} style={styles.row}>
            <View style={styles.rowTop}>
              <Text style={styles.name}>{t.label}</Text>
              <Text style={styles.accuracy}>{pct}%</Text>
            </View>
            <View style={styles.track}>
              <View
                style={[
                  styles.fill,
                  {
                    width: `${pct}%`,
                    backgroundColor: colorForAccuracy(pct),
                  },
                ]}
              />
            </View>
          </View>
        );
      })}
    </View>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    section: { gap: 12 },
    eyebrow: {
      fontFamily: fontFamily.monoMedium,
      fontSize: 11,
      letterSpacing: 1.4,
      color: colors.textMuted,
      marginBottom: 12,
    },
    row: {
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: colors.borderSoft,
      gap: 6,
    },
    rowTop: {
      flexDirection: "row",
      justifyContent: "space-between",
    },
    name: {
      fontFamily: fontFamily.semiBold,
      fontSize: 15,
      color: colors.textPrimary,
    },
    accuracy: {
      fontFamily: fontFamily.monoMedium,
      fontSize: 13,
      color: colors.textMuted,
    },
    track: {
      height: 6,
      backgroundColor: colors.surfaceMuted,
      borderRadius: radius.pill,
      overflow: "hidden",
    },
    fill: { height: "100%", borderRadius: radius.pill },
  });
}
